import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, Clock, CheckCircle, ChefHat, Package,
  Trash2, Phone, Mail, User, RefreshCw,
  LogOut, AlertCircle, Bell, Volume2, VolumeX
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  getOrders,
  fetchOrdersFromServer,
  updateOrderStatus,
  clearCompletedOrders,
  type Order,
} from '@/store/orderStore';

export default function AdminPanel() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'preparing' | 'ready' | 'completed'>('all');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');

  const ADMIN_PASSWORD = 'KPC2025!';

  const [previousOrderCount, setPreviousOrderCount] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [newOrderIds, setNewOrderIds] = useState<string[]>([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [audioContextReady, setAudioContextReady] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  // Initialize audio context on first user interaction (required by browsers)
  async function initializeAudio() {
    try {
      // Test play a silent sound to unlock audio context
      const testAudio = new Audio('/assets/order-alert.mp3');
      testAudio.volume = 0.01;
      await testAudio.play();
      testAudio.pause();
      setAudioContextReady(true);
      toast.success('Sound alerts activated!');
    } catch {
      toast.error('Could not activate sound. Tap the button again.');
    }
  }

  // Load orders from SERVER (syncs across all devices)
  async function loadOrdersFromServer() {
    try {
      const serverOrders = await fetchOrdersFromServer();
      setIsOnline(true);

      const currentPending = serverOrders.filter(o => o.status === 'pending');

      // Check for NEW orders (alert!)
      if (currentPending.length > previousOrderCount && previousOrderCount > 0) {
        const oldOrderIds = orders.map(o => o.id);
        const newOrders = currentPending.filter(o => !oldOrderIds.includes(o.id));

        if (newOrders.length > 0) {
          // Play alert sound (only if user activated audio)
          if (soundEnabled && audioContextReady) {
            try {
              const audio = new Audio('/assets/order-alert.mp3');
              audio.volume = 1.0;
              audio.play().catch(() => {});
            } catch {
              // Sound blocked by browser
            }
          }

          // Show notification banner
          setNewOrderIds(newOrders.map(o => o.id));
          setShowNotification(true);

          // Toast notification
          toast.success(`NEW ORDER! #${newOrders[0].id.slice(-4)}`, {
            description: `${newOrders[0].customerName} — $${newOrders[0].total.toFixed(2)}`,
            duration: 15000,
          });

          // Blink tab title
          const originalTitle = document.title;
          const blinkInterval = setInterval(() => {
            document.title = document.title === 'NEW ORDER!' ? originalTitle : 'NEW ORDER!';
          }, 500);

          setTimeout(() => {
            clearInterval(blinkInterval);
            document.title = originalTitle;
          }, 10000);

          // Hide banner after 10 seconds
          setTimeout(() => {
            setShowNotification(false);
            setNewOrderIds([]);
          }, 10000);
        }
      }

      setOrders(serverOrders);
      setPreviousOrderCount(currentPending.length);
    } catch {
      setIsOnline(false);
      // Fallback to local
      const localOrders = getOrders();
      setOrders(localOrders);
      const pendingCount = localOrders.filter(o => o.status === 'pending').length;
      setPreviousOrderCount(pendingCount);
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      // Initial load
      loadOrdersFromServer();

      // Poll server every 10 seconds for new orders
      const interval = setInterval(() => {
        loadOrdersFromServer();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  function handleLogin() {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Welcome to KPC Admin');
    } else {
      toast.error('Incorrect password');
    }
  }

  function handleStatusChange(orderId: string, status: Order['status']) {
    updateOrderStatus(orderId, status);
    // Refresh from server
    loadOrdersFromServer();
  }

  function handleClearCompleted() {
    if (confirm('Are you sure you want to clear all completed orders?')) {
      clearCompletedOrders();
      loadOrdersFromServer();
    }
  }

  function getStatusIcon(status: Order['status']) {
    switch (status) {
      case 'pending': return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'preparing': return <ChefHat className="w-5 h-5 text-orange-500" />;
      case 'ready': return <Package className="w-5 h-5 text-green-500" />;
      case 'completed': return <CheckCircle className="w-5 h-5 text-gray-500" />;
    }
  }

  function getStatusText(status: Order['status']) {
    switch (status) {
      case 'pending': return 'New Order';
      case 'preparing': return 'Preparing';
      case 'ready': return 'Ready for Pickup';
      case 'completed': return 'Completed';
    }
  }

  const filteredOrders = filter === 'all'
    ? orders
    : orders.filter(o => o.status === filter);

  const pendingCount = orders.filter(o => o.status === 'pending').length;
  const preparingCount = orders.filter(o => o.status === 'preparing').length;
  const readyCount = orders.filter(o => o.status === 'ready').length;
  const completedCount = orders.filter(o => o.status === 'completed').length;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background korean-pattern flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 max-w-sm w-full">
          <div className="text-center mb-6">
            <img src="/images/logo.png" alt="KPC" className="h-20 w-auto mx-auto mb-4 object-contain" />
            <h1 className="text-2xl font-bold text-white font-['Orbitron']">KPC Admin</h1>
            <p className="text-muted-foreground mt-2">Staff Access Only</p>
          </div>

          <div className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              placeholder="Enter password"
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-white placeholder:text-muted-foreground focus:border-[#FFD700] focus:outline-none"
            />
            <Button onClick={handleLogin} className="w-full btn-neon text-white">
              Login
            </Button>
          </div>

          <button
            onClick={() => navigate('/')}
            className="mt-6 text-sm text-muted-foreground hover:text-[#FFD700] transition-colors flex items-center justify-center gap-2 w-full"
          >
            <LogOut className="w-4 h-4" />
            Back to Website
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background korean-pattern">
      {/* New Order Alert Banner */}
      {showNotification && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-red-600 text-white py-4 px-4 animate-pulse shadow-lg shadow-red-600/50">
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-3">
            <Bell className="w-7 h-7 animate-bounce" />
            <span className="font-bold text-xl uppercase tracking-wide">New Order Received!</span>
            <span className="font-mono bg-white/20 px-3 py-1 rounded">#{newOrderIds.map(id => id.slice(-4)).join(', #')}</span>
            <button
              onClick={() => { setShowNotification(false); setNewOrderIds([]); }}
              className="ml-4 text-sm underline hover:no-underline opacity-80 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`sticky z-50 glass-card border-b border-border ${showNotification ? 'top-16' : 'top-0'}`}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/logo.png" alt="KPC" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="text-lg font-bold text-white font-['Orbitron']">Order Manager</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                {orders.length} total orders
                {!isOnline && <span className="text-red-400">(Offline)</span>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Audio activation button (required by browsers) */}
            {!audioContextReady && (
              <Button
                size="sm"
                onClick={initializeAudio}
                className="bg-red-600 hover:bg-red-700 text-white animate-pulse"
              >
                <Bell className="w-4 h-4 mr-1" />
                Activate Alerts
              </Button>
            )}

            {/* Sound toggle */}
            {audioContextReady && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`border-border ${soundEnabled ? 'text-[#FFD700]' : 'text-gray-500'}`}
                title={soundEnabled ? 'Sound On' : 'Sound Off'}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => loadOrdersFromServer()}
              className="border-border"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearCompleted}
              className="border-border text-red-400 hover:text-red-300"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Clear Done
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="border-border"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <button
            onClick={() => setFilter('pending')}
            className={`glass-card rounded-xl p-4 text-center transition-all ${filter === 'pending' ? 'ring-2 ring-yellow-500' : ''} ${pendingCount > 0 ? 'animate-pulse' : ''}`}
          >
            <AlertCircle className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{pendingCount}</p>
            <p className="text-xs text-muted-foreground">New</p>
          </button>

          <button
            onClick={() => setFilter('preparing')}
            className={`glass-card rounded-xl p-4 text-center transition-all ${filter === 'preparing' ? 'ring-2 ring-orange-500' : ''}`}
          >
            <ChefHat className="w-6 h-6 text-orange-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{preparingCount}</p>
            <p className="text-xs text-muted-foreground">Preparing</p>
          </button>

          <button
            onClick={() => setFilter('ready')}
            className={`glass-card rounded-xl p-4 text-center transition-all ${filter === 'ready' ? 'ring-2 ring-green-500' : ''}`}
          >
            <Package className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{readyCount}</p>
            <p className="text-xs text-muted-foreground">Ready</p>
          </button>

          <button
            onClick={() => setFilter('completed')}
            className={`glass-card rounded-xl p-4 text-center transition-all ${filter === 'completed' ? 'ring-2 ring-gray-500' : ''}`}
          >
            <CheckCircle className="w-6 h-6 text-gray-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{completedCount}</p>
            <p className="text-xs text-muted-foreground">Done</p>
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {(['all', 'pending', 'preparing', 'ready', 'completed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-all whitespace-nowrap ${
                filter === f
                  ? 'bg-[#FFD700] text-black'
                  : 'bg-card border border-border text-muted-foreground hover:text-white'
              }`}
            >
              {f === 'all' ? 'All Orders' : f}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="glass-card rounded-xl p-12 text-center">
            <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-white text-lg">No orders found</p>
            <p className="text-muted-foreground text-sm mt-1">
              {filter === 'all'
                ? 'Orders from all devices will appear here automatically. Make sure you are online.'
                : `No ${filter} orders.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <div
                key={order.id}
                className={`glass-card rounded-xl p-5 transition-all ${
                  order.status === 'pending' ? 'ring-1 ring-yellow-500/50' : ''
                }`}
              >
                {/* Order Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(order.status)}
                    <div>
                      <p className="font-bold text-white font-mono">#{order.id.slice(-6)}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(order.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-xl font-bold text-[#FFD700]">${order.total.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      order.status === 'preparing' ? 'bg-orange-500/20 text-orange-400' :
                      order.status === 'ready' ? 'bg-green-500/20 text-green-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {getStatusText(order.status)}
                    </span>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="bg-black/30 rounded-lg p-3 mb-4 space-y-2">
                  <p className="text-sm text-muted-foreground mb-2 font-medium">Customer</p>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-[#FFD700]" />
                    <span className="text-white">{order.customerName || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-[#FFD700]" />
                    <a href={`tel:${order.customerPhone}`} className="text-white hover:text-[#FFD700]">
                      {order.customerPhone || 'N/A'}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-[#FFD700]" />
                    <span className="text-white">{order.customerEmail || 'N/A'}</span>
                  </div>
                </div>

                {/* Order Items */}
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground mb-2 font-medium">Order Items</p>
                  <div className="space-y-1">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-white">{item.name} x{item.quantity}</span>
                        <span className="text-[#FFD700]">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator className="bg-border my-3" />

                {/* Action Buttons */}
                <div className="flex gap-2 flex-wrap">
                  {order.status === 'pending' && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusChange(order.id, 'preparing')}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <ChefHat className="w-4 h-4 mr-1" />
                      Start Preparing
                    </Button>
                  )}

                  {order.status === 'preparing' && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusChange(order.id, 'ready')}
                      className="bg-green-500 hover:bg-green-600 text-white"
                    >
                      <Package className="w-4 h-4 mr-1" />
                      Mark Ready
                    </Button>
                  )}

                  {order.status === 'ready' && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusChange(order.id, 'completed')}
                      variant="outline"
                      className="border-green-500 text-green-400 hover:bg-green-500/20"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Complete Order
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const text = `Order: ${order.id}\nCustomer: ${order.customerName}\nPhone: ${order.customerPhone}\nItems:\n${order.items.map(i => `- ${i.name} x${i.quantity}`).join('\n')}\nTotal: $${order.total.toFixed(2)}`;
                      navigator.clipboard.writeText(text);
                      toast.success('Order details copied');
                    }}
                    className="border-border ml-auto"
                  >
                    Copy Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Audio hint */}
        {!audioContextReady && (
          <div className="glass-card rounded-xl p-4 mt-6 text-center border border-yellow-500/30">
            <Bell className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <p className="text-yellow-400 text-sm font-medium">
              Tap "Activate Alerts" button above to enable sound notifications
            </p>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8 pb-4">
          Auto-refreshing every 10 seconds. Orders sync across all devices.
        </p>
      </div>
    </div>
  );
}
